import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import {
  CreateOrganizationMemberSchema,
  OrganizationOutFormSchema,
  UpdateOrganizationMemberSchema,
} from 'src/Superadmin/index.types';
import { client } from 'src/utils/api-client';

// Helper function to invalidate all organization member queries
const invalidateOrganizationMemberQueries = (queryClient: any) => {
  queryClient.invalidateQueries(['organization-members-paginated']);
  queryClient.invalidateQueries(['organization-members-legacy']);
  queryClient.invalidateQueries('organization-members');
};

// eslint-disable-next-line import/prefer-default-export
export const useOrganizationMember = (memberId: number) => {
  const url = 'web/settings_v2/organization-members';
  const queryClient = useQueryClient();
  const { t } = useTranslation(undefined, {
    keyPrefix: 'superadmin.settings.tabs.users.usersTable.toasts.success',
  });

  const updateOrganizationMember = useMutation({
    mutationFn: (data: Omit<UpdateOrganizationMemberSchema, 'user_id'>) =>
      client.put<UpdateOrganizationMemberSchema>(`${url}/${data.id}`, {
        ...data,
        organization_id: data.organizationId,
        user_id: memberId,
      }),
    onSuccess: () => {
      invalidateOrganizationMemberQueries(queryClient);
      toast.success(t('update') as string);
    },
  });

  const deleteOrganizationMember = useMutation({
    mutationFn: (id: string) => client.delete(`${url}/${id}`),
    onSuccess: () => {
      invalidateOrganizationMemberQueries(queryClient);
      toast.success(t('delete') as string);
    },
  });

  const createOrganizationMember = useMutation({
    mutationFn: (data: Omit<CreateOrganizationMemberSchema, 'user_id'>) =>
      client.post<CreateOrganizationMemberSchema>(url, {
        ...data,
        organization_id: data.organizationId,
        user_id: memberId,
      }),
    onSuccess: () => {
      invalidateOrganizationMemberQueries(queryClient);
      toast.success(t('post') as string);
    },
  });

  const { data } = useQuery('organization-groups', () =>
    client
      .get<OrganizationOutFormSchema[]>('web/settings_v2/organizations')
      .then((res) => res?.data || [])
  );

  const updateUserBranches = useMutation({
    mutationFn: ({ branch, value }: { branch: string; value: boolean }) =>
      client.put(`web/settings_v2/users/${memberId}/branches`, {
        branch,
        value,
      }),
    onSuccess: () => {
      // Invalidate all organization member queries to refresh the data
      invalidateOrganizationMemberQueries(queryClient);
      toast.success(t('update') as string);
    },
  });

  const updateUserAndOrganizationBranch = useMutation({
    mutationFn: ({
      organizationId,
      branch,
      value,
    }: {
      organizationId: number;
      branch: string;
      value: boolean;
    }) =>
      client.put(`web/settings_v2/users/${memberId}/branches/with-organization`, {
        organization_id: organizationId,
        branch,
        value,
      }),
    onSuccess: () => {
      invalidateOrganizationMemberQueries(queryClient);
      toast.success(t('update') as string);
    },
  });

  const organizations = data || [];

  return {
    organizations,
    createOrganizationMember,
    updateOrganizationMember,
    deleteOrganizationMember,
    updateUserBranches,
    updateUserAndOrganizationBranch,
  };
};
